/**
 * Service-worker-aware reload helper (user-approved policy 1 — the
 * upgrade-required surface must be able to land the fresh build, not a
 * stale cached shell).
 *
 * Standalone (no React hook) so the eager App-level upgrade-required
 * gate can call it without pulling the heavier `usePwaInstall` hook —
 * and its SW registration side effect — into the cold-login entry
 * chunk. The hook in `lib/hooks/pwa_install.ts` stays the owner of SW
 * registration + the `waiting`/`controlling` lifecycle; this helper
 * only drives the user-confirmed apply from the upgrade modal.
 *
 * Behaviour:
 *   1. If a waiting service worker is precached, message it
 *      `SKIP_WAITING` (the same message `usePwaInstall.applyUpdate`
 *      sends). The registered `controlling` listener then reloads the
 *      page onto the new shell.
 *   2. Always arm a fallback hard reload so the user is never wedged on
 *      a stale page when there is no waiting worker (the common case —
 *      the build is stale but the SW hasn't precached a newer one yet),
 *      when the SW lifecycle hangs, or when SW is unsupported / dev.
 */

/** Fallback hard-reload delay (ms) when a waiting SW is told to skip
 *  waiting — long enough for a healthy `controlling` event to reload
 *  first, short enough that a hung SW doesn't strand the user. Mirrors
 *  the `usePwaInstall.applyUpdate` timeout. */
const FALLBACK_RELOAD_MS = 1_500;

export function reloadForUpgrade(): void {
  if (typeof window === "undefined") return;
  const reload = (): void => window.location.reload();

  const swContainer =
    typeof navigator !== "undefined" && "serviceWorker" in navigator
      ? navigator.serviceWorker
      : null;
  if (swContainer === null) {
    reload();
    return;
  }

  void swContainer
    .getRegistration()
    .then((reg) => {
      const waiting = reg?.waiting ?? null;
      if (waiting !== null) {
        // Activate the precached build; the hook's `controlling`
        // listener reloads onto it.
        waiting.postMessage({ type: "SKIP_WAITING" });
        window.setTimeout(reload, FALLBACK_RELOAD_MS);
        return;
      }
      // No waiting worker — just refresh; the browser fetches the
      // fresh index + assets (SW precache revalidates on the new load).
      reload();
    })
    .catch(() => {
      reload();
    });
}
